// Strict full ISO-8601 *datetime* (date + time + zone). Matches what the
// backend's `t.Date()` response fields serialize to (e.g.
// "2026-06-16T00:00:00.000Z" or "...+07:00"). Deliberately requires the time
// component + zone so plain "2026-06-16" dates never match.
const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

/**
 * Whether an object key names a date field. The backend names every `t.Date()`
 * field with an `At` suffix (createdAt, updatedAt, paidAt, expiresAt, sendAt,
 * tokenInvalidAt, …) with the sole exception of `dateOfBirth`. Restricting
 * revival to these keys is what makes it safe: free-text, user-controlled
 * fields (a chat message `text`, a customer `name`/`notes`, a product name)
 * are never converted even if their value happens to be exactly an ISO
 * datetime string.
 */
function isDateKey(key: string): boolean {
  return key.endsWith("At") || key === "dateOfBirth";
}

/**
 * Deep-clone `value`, replacing ISO-8601 datetime *strings at date-named keys*
 * with `Date`. Eden types `t.Date()` response fields as `Date` (the TypeBox
 * static type) but the JSON wire carries ISO strings, so without this the
 * runtime value would be a string while the type claims `Date`. Reviving at the
 * `edenData` seam makes runtime match the type. Pure — never mutates the input.
 *
 * Key-scoped on purpose: a value-only heuristic (which is what Eden's built-in
 * `parseDate` does) would silently corrupt any free-text field that happened to
 * hold a date-like string, so we disable that and revive by key here instead.
 */
export function reviveDates(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(reviveDates);
  }
  if (value !== null && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      if (typeof val === "string" && isDateKey(key) && ISO_DATETIME.test(val)) {
        out[key] = new Date(val);
      } else {
        out[key] = reviveDates(val);
      }
    }
    return out;
  }
  return value;
}
